import { useEffect } from "react";
import { useDraftReturn } from "./use-draft-return";
import { useSetShippingRate } from "./use-set-shipping-rate";
import { useShouldPayReshipping } from "./use-should-pay-reshipping";

/**
 * Amount of the selected reshipping rate to charge the customer, in cents.
 *
 * Zero unless the shop bills the reshipping: a complement to pay on the
 * returned items never turns the shipping of the new items into a billable
 * line.
 */
export function useReshippingAmount(orderId: string, shopId: string): number {
  const draft = useDraftReturn(orderId);
  const shouldPayReshipping = useShouldPayReshipping(orderId, shopId);

  return shouldPayReshipping ? (draft?.shippingRateAmount ?? 0) : 0;
}

/**
 * Drops the selected rate from the draft as soon as the reshipping stops being
 * billable — the customer swapped a resolution for a refund, or picked a reason
 * that waives the reshipping, after passing through the checkout.
 *
 * The draft is what the PaymentIntent's `rateHandle` and the return request's
 * `shippingLine` are built from, so clearing it here is what keeps those two
 * from billing a rate the receipt no longer shows. Mounted once per order, in
 * the order layout, since the customer can leave the checkout by any route.
 */
export function useSyncReshippingRate(orderId: string, shopId: string): void {
  const draft = useDraftReturn(orderId);
  const shouldPayReshipping = useShouldPayReshipping(orderId, shopId);
  const setShippingRate = useSetShippingRate(orderId);
  const hasRate = draft?.shippingRateId != null;

  useEffect(() => {
    if (!shouldPayReshipping && hasRate) setShippingRate(null);
  }, [shouldPayReshipping, hasRate, setShippingRate]);
}
